import { useSupabaseClient, useState, ref } from '#imports';
import type { Tables } from '~~/types/database.types.ts';
import uniqBy from 'lodash.uniqby'

interface Spot extends Tables<'spots'> {
  categories: Tables<'categories'>;
  cities: Tables<'cities'>;
  map_id: {
    title: string;
    current_slug: string;
  };
};

type SpotBookmark = Tables<'spot_bookmarks'> & {
  spot: Spot;
};

type Map = Tables<'maps'> & {
  spot: Tables<'spots'> & {
    categories: Tables<'categories'>;
    cities: Tables<'cities'>;
  }[];
};

type MapBookmark = Tables<'map_bookmarks'> & {
  map: Map;
};

export const useAtlas = () => {
  const spotBookmarks = useState<Spot[]>('spotBookmarks', () => []);
  const spotCategories = useState('spotCategories', () => []);
  const spotDestinations = useState('spotDestinations', () => []);
  const filteredSpots = useState<Spot[]>('filteredSpots', () => []);
  const mapBookmarks = useState<Map[]>('mapBookmarks', () => []);
  const atlasMarkers = useState('atlasMarkers', () => []);
  const mapCategories = useState('mapCategories', () => []);
  const mapDestinations = useState('mapDestinations', () => []);
  const filteredMaps = useState<Map[]>('filteredMaps', () => []);
  const mapCategoryFilter = useState<Tables<'categories'>[]>('mapCategoryFilter', () => []);
  const mapDestinationFilter = useState<Tables<'cities'>[]>('mapDestinationFilter', () => []);
  const spotDestinationFilter = useState<Tables<'cities'>[]>('spotDestinationFilter', () => []);
  const spotCategoryFilter = useState<Tables<'categories'>[]>('spotCategoryFilter', () => []);
  const hightlightSpot = useState('highlightSpot', () => null);
  const isMapView = useState('isMapView', () => false);

  const supabase = useSupabaseClient();

  const fetchSpots = async () => {
    const { data } = await supabase
      .from('spot_bookmarks')
      .select(`
        *,
        spot:spots!inner(
          *,
          categories:category_id (
            id,
            name,
            icon
          ),
          cities:cities_id (
            id,
            name
          ),
          map_id:maps(
            title,
            current_slug
          )
        )
      `)

    if (!data) {
       spotBookmarks.value = [];
       spotCategories.value = [];
       spotDestinations.value = [];
       filteredSpots.value = [];
       return;
     }

    const spots: Spot[] = data?.map((bookmark: SpotBookmark) => ({
      ...bookmark.spot
    }))


    const categories:Tables<'categories'>[] = []
    const destinations:Tables<'cities'>[] = []

    data?.map((bookmark: SpotBookmark) => {
      categories.push(bookmark.spot.categories)
      destinations.push(bookmark.spot.cities)
    })

    spotCategories.value = uniqBy(categories.filter(Boolean), 'id');
    spotDestinations.value = uniqBy(destinations.filter(Boolean), 'id');

    spotBookmarks.value = spots;
    filteredSpots.value = spots;
    setAtlasMarkers(spots);
  };

  const filterSpots = (categoryFilter: string[], destinationFilter: string[]) => {
    if (!categoryFilter.length && !destinationFilter.length) {
      filteredSpots.value = spotBookmarks.value;
      setAtlasMarkers(spotBookmarks.value);
      return;
    }
    filteredSpots.value = spotBookmarks.value.filter(bookmark => {
      const matchesCategory = !categoryFilter.length ||
        categoryFilter.some(category => category === bookmark.category_id);
      const matchesDestination = !destinationFilter.length ||
        destinationFilter.some(destination => destination === bookmark.cities_id);
      return matchesCategory && matchesDestination;
    });

    setAtlasMarkers(filteredSpots.value);
  };

  const fetchMaps = async () => {
    const { data } = await supabase
      .from('map_bookmarks')
      .select(`
        *,
        map:map_id!inner(
          *,
          profiles:user_id!inner(
            id,
            summary,
            mappr_handle,
            profile_image_url,
            display_name,
            maps_count:maps(count)
          ),
          spot:spots!inner(
            *,
            categories:category_id (
              id,
              name,
              icon
            ),
            cities:cities_id (
              id,
              name
            )
          )
        )
      `)
      .eq('map.is_published', true)

    const maps: Map = data?.map((bookmark: MapBookmark) => ({
      ...bookmark.map
    }))

    const spots: Tables<Spot>[] = []
    maps.map((map: Map) => {
      map.spot.map(spot => {
        spots.push(spot)
      })
    })

    const categories: Tables<'categories'>[] = []
    const destinations: Tables<'cities'>[] = []

    spots?.map(spot => {
      categories.push(spot.categories)
      destinations.push(spot.cities)
    })

    mapCategories.value = uniqBy(categories.filter(Boolean), 'id');
    mapDestinations.value = uniqBy(destinations.filter(Boolean), 'id');
    setAtlasMarkers(spots)
    mapBookmarks.value = maps;
    filteredMaps.value = maps;
  };

  const filterMaps = (categoryFilter: string[], destinationFilter: string[]) => {
    if (!categoryFilter.length && !destinationFilter.length) {
      filteredMaps.value = mapBookmarks.value;
      atlasMarkers.value = mapBookmarks.value.flatMap(map => map.spot);
      return;
    }

    filteredMaps.value = mapBookmarks.value.filter(map => {
      return map.spot.some(spot => {
        const matchesCategory = !categoryFilter.length ||
          categoryFilter.some(category => category === spot.category_id);
        const matchesDestination = !destinationFilter.length ||
          destinationFilter.some(destination => destination === spot.cities_id);
        return matchesCategory && matchesDestination;
      });
    });

    const filteredMarkers = filteredMaps.value.flatMap(map => map.spot).filter(spot => {
       const matchesCategory = !categoryFilter.length ||
         categoryFilter.some(category => category === spot.category_id);
       const matchesDestination = !destinationFilter.length ||
         destinationFilter.some(destination => destination === spot.cities_id);
       return matchesCategory && matchesDestination;
     });

    setAtlasMarkers(filteredMarkers);
  };

  const setAtlasMarkers = (newMarkers) => {
    atlasMarkers.value = newMarkers;
  }

  const centerToSpot = (spotId: string) => {
    hightlightSpot.value = spotId;
  }

  const setIsMapView = (value: boolean) => {
    isMapView.value = value;
  }

  return {
    spotBookmarks,
    spotCategories,
    spotDestinations,
    filteredSpots,
    filterSpots,
    hightlightSpot,
    centerToSpot,
    mapBookmarks,
    filteredMaps,
    atlasMarkers,
    setIsMapView,
    setAtlasMarkers,
    mapCategories,
    mapDestinations,
    isMapView,
    fetchSpots,
    fetchMaps,
    filterMaps,
    mapCategoryFilter,
    mapDestinationFilter,
    spotCategoryFilter,
    spotDestinationFilter
  };
};
